/**
 * Cron expression parsing and formatting utilities.
 */

const HOUR_INTERVALS = [1, 2, 4, 6, 8, 12];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function parseCronToFriendly(cron) {
  if (!cron || typeof cron !== 'string') return {
    preset: 'daily', hour: 9, minute: 0, dayOfWeek: 1, dayOfMonth: 1, intervalHours: 4,
  };
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return { preset: 'custom', cron };
  const [min, hour, dayOfMonth, month, dow] = parts;
  if (month !== '*') return { preset: 'custom', cron };
  if (hour?.startsWith('*/') && min === '0' && dayOfMonth === '*' && dow === '*') {
    const interval = parseInt(parts[1].replace('*/', ''), 10);
    return { preset: 'hourly', intervalHours: HOUR_INTERVALS.includes(interval) ? interval : 4 };
  }
  const minute = /^\d{1,2}$/.test(min) ? parseInt(min, 10) : Number.NaN;
  const hourNum = /^\d{1,2}$/.test(hour) ? parseInt(hour, 10) : Number.NaN;
  if (!Number.isInteger(minute) || minute < 0 || minute > 59
    || !Number.isInteger(hourNum) || hourNum < 0 || hourNum > 23) {
    return { preset: 'custom', cron };
  }
  if (dayOfMonth === '*' && dow === '1-5') return { preset: 'weekdays', hour: hourNum, minute };
  if (dayOfMonth === '*' && dow === '*') return { preset: 'daily', hour: hourNum, minute };
  const dowNum = /^\d$/.test(dow) ? parseInt(dow, 10) : Number.NaN;
  if (dayOfMonth === '*' && Number.isInteger(dowNum) && dowNum >= 0 && dowNum <= 7) {
    return { preset: 'weekly', hour: hourNum, minute, dayOfWeek: dowNum === 7 ? 0 : dowNum };
  }
  const dayNum = /^\d{1,2}$/.test(dayOfMonth) ? parseInt(dayOfMonth, 10) : Number.NaN;
  if (dow === '*' && Number.isInteger(dayNum) && dayNum >= 1 && dayNum <= 31) {
    return { preset: 'monthly', hour: hourNum, minute, dayOfMonth: dayNum };
  }
  return { preset: 'custom', cron };
}

export function buildCronFromFriendly({
  preset,
  hour = 9,
  minute = 0,
  dayOfWeek = 1,
  dayOfMonth = 1,
  intervalHours = 4,
}) {
  if (preset === 'custom') return null;
  const m = minute ?? 0;
  const h = hour ?? 9;
  if (preset === 'daily') return `${m} ${h} * * *`;
  if (preset === 'weekdays') return `${m} ${h} * * 1-5`;
  if (preset === 'weekly') return `${m} ${h} * * ${dayOfWeek}`;
  if (preset === 'monthly') return `${m} ${h} ${dayOfMonth} * *`;
  if (preset === 'hourly') return `0 */${intervalHours} * * *`;
  return null;
}

function formatTime(hour, minute) {
  const h = hour ?? 9;
  const m = minute ?? 0;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

function ordinal(value) {
  const remainder100 = value % 100;
  if (remainder100 >= 11 && remainder100 <= 13) return `${value}th`;
  if (value % 10 === 1) return `${value}st`;
  if (value % 10 === 2) return `${value}nd`;
  if (value % 10 === 3) return `${value}rd`;
  return `${value}th`;
}

export function formatTimeZoneToHumanReadable(timeZone) {
  const normalized = String(timeZone || '').trim();
  if (!normalized) return '';
  if (normalized === 'UTC') return 'UTC';
  try {
    const name = new Intl.DateTimeFormat(undefined, {
      timeZone: normalized,
      timeZoneName: 'longGeneric',
    }).formatToParts(new Date()).find((part) => part.type === 'timeZoneName')?.value;
    if (name && name !== normalized) return `${name} (${normalized.replaceAll('_', ' ')})`;
  } catch {
    // Preserve the configured IANA timezone when the browser cannot format it.
  }
  return normalized.replaceAll('_', ' ');
}

export function formatScheduleToHumanReadable(cron, timeZone = '') {
  if (!cron || typeof cron !== 'string') return 'Schedule';
  const parsed = parseCronToFriendly(cron);
  let summary = 'Custom recurring schedule';
  if (parsed.preset === 'daily') summary = `Every day at ${formatTime(parsed.hour, parsed.minute)}`;
  if (parsed.preset === 'weekdays') summary = `Every weekday at ${formatTime(parsed.hour, parsed.minute)}`;
  if (parsed.preset === 'weekly') summary = `Every ${DAY_NAMES[parsed.dayOfWeek ?? 1]} at ${formatTime(parsed.hour, parsed.minute)}`;
  if (parsed.preset === 'monthly') summary = `On the ${ordinal(parsed.dayOfMonth ?? 1)} of every month at ${formatTime(parsed.hour, parsed.minute)}`;
  if (parsed.preset === 'hourly') {
    summary = parsed.intervalHours === 1 ? 'Every hour' : `Every ${parsed.intervalHours ?? 4} hours`;
  }
  const zone = formatTimeZoneToHumanReadable(timeZone);
  return zone ? `${summary} in ${zone}` : summary;
}
